/*******************************************************************************
* Licensed Materials - Property of HCL
* (c) Copyright HCL Technologies Ltd. 2020, 2023. All Rights Reserved.

* Note to U.S. Government Users Restricted Rights:
* Use, duplication or disclosure restricted by GSA ADP Schedule
**************************************************************/

/**
 * Component that displays 3 bubbles with task status
 * required params:
 */

import React from "react";
import { useSelector } from "react-redux";
import { BallsGraph, Button, Text, BackgroundPanel } from "vr-react-components";

const getColor = (status) => {
    return status.includes("No")
        ? "#05be62"
        : status.includes("High")
            ? "#f95454"
            : "#eea600";
};

const getLabel = (status) => {
    return status.includes("No")
        ? "No risk"
        : status.includes("High")
            ? "High risk"
            : "Potential risk";
};

const CriticalJobsGraph = ({
    width,
    height,
    onOpen,
    textSizeDecimal,
    isNotClickable,
    ...otherAttributes
}) => {
    const canModify = useSelector((state) => state.private.device.canModify);
    const currentRoom = useSelector((state) => state.room.history[0].ID);
    const criticalJobsData = useSelector((state) => state[currentRoom].criticalJobsData);
    const highRisk = useSelector((state) => state[currentRoom].criticalJobsData["Critical Jobs Info High Risk"]);
    const potentialRisk = useSelector((state) => state[currentRoom].criticalJobsData["Critical Jobs Info Potential Risk"]);
    const noRisk = useSelector((state) => state[currentRoom].criticalJobsData["Critical Jobs Info No Risk"]);

    const isPlaceholder = useSelector((state) => state.user.engines.selected.length === 0);
    const noCriticalJob = (highRisk.length + potentialRisk.length + noRisk.length) === 0;

    let summaryData = [
        { value: 0, color: "#363636", label: "High risk" },
        { value: 0, color: "#363636", label: "Potential risk" },
        { value: 0, color: "#363636", label: "No risk" },
    ];
    if ( !noCriticalJob ) {
        summaryData = Object.keys(criticalJobsData).map((key) => {
            return {
                value: criticalJobsData[key].length ? criticalJobsData[key].length : 0,
                color: getColor(key),
                label: getLabel(key),
            };
        });
    } else if (isPlaceholder)
        return (
            <BackgroundPanel
                backgroundColor="#262626"
                position={`0 0 ${width / 500}`}
                width={width}
                height={height}
                {...otherAttributes}
            >
                <Text
                    width={width}
                    sizeDecimal={27 / width}
                    text="No data available"
                    align="center"
                />
            </BackgroundPanel>
        );

    return (
        <BackgroundPanel
            backgroundColor="#262626"
            position={`0 0 ${width / 500}`}
            width={width}
            height={height}
            {...otherAttributes}
        >
            {isNotClickable && (
                <Text
                    width={width}
                    sizeDecimal={textSizeDecimal}
                    text="Critical jobs status (by risk)"
                    align="left"
                    position={`${-width * 0.45} ${height * 0.45} 0`}
                    baseline="top"
                />
            )}
            <BallsGraph width={width} height={height * 0.8} data={summaryData} />
            {!isNotClickable && (
                <Button
                    disabled={noCriticalJob}
                    width={width * 0.8}
                    height={height * 0.1}
                    text="See critical jobs list"
                    type={Button.constants.primary}
                    textSizeDecimal={27 / width}
                    aframeClasses={canModify ? "collidable" : ""}
                    position={`0 ${-height / 2 + height * 0.1} ${width * 0.001}`}
                    onClickHandler={() => { 
                        if ( highRisk.length + potentialRisk.length + noRisk.length > 0 ) onOpen();
                    }}
                />
            )}
        </BackgroundPanel>
    );
};

export default CriticalJobsGraph;